import { useEffect, useState } from "react";
import { useThemeContext } from "../../../../context/ThemeContext";
import { Download, AlertCircle, Info, CheckCircle } from "lucide-react";

const Step2AssessmentSetup = ({
  assessmentSetup,
  setAssessmentSetup,
  onGenerateTemplate,
  initialData = null,
  onBack,
  onNext,
}) => {
  const { darkMode } = useThemeContext();
  const [validationErrors, setValidationErrors] = useState({});
  useEffect(() => {
    if (initialData) {
      setAssessmentSetup(initialData);
    }
  }, [initialData, setAssessmentSetup]);

  const handleInputChange = (field, value) => {
    if (value === "" || (!isNaN(value) && value >= 0)) {
      setAssessmentSetup({
        ...assessmentSetup,
        [field]: value === "" ? "" : parseInt(value),
      });

      if (validationErrors[field]) {
        const newErrors = { ...validationErrors };
        delete newErrors[field];
        setValidationErrors(newErrors);
      }
    }
  };

  const validateInputs = () => {
    const errors = {};
    const { assignments, classTests, projects } = assessmentSetup;

    if (assignments === "" || assignments < 0 || assignments > 10) {
      errors.assignments = "Must be 0-10";
    }

    if (classTests === "" || classTests < 0 || classTests > 5) {
      errors.classTests = "Must be 0-5";
    }

    if (projects === "" || projects < 0 || projects > 3) {
      errors.projects = "Must be 0-3";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    const processedSetup = {
      assignments:
        assessmentSetup.assignments === "" ? 0 : assessmentSetup.assignments,
      classTests:
        assessmentSetup.classTests === "" ? 0 : assessmentSetup.classTests,
      projects: assessmentSetup.projects === "" ? 0 : assessmentSetup.projects,
      includePRS: assessmentSetup.includePRS,
    };

    if (validateInputs()) {
      setAssessmentSetup(processedSetup);
      onNext();
    }
  };

  const totalAssessments =
    (assessmentSetup.assignments === "" ? 0 : assessmentSetup.assignments) +
    (assessmentSetup.classTests === "" ? 0 : assessmentSetup.classTests) +
    (assessmentSetup.projects === "" ? 0 : assessmentSetup.projects) +
    (assessmentSetup.includePRS ? 1 : 0);

  return (
    <div
      className={`p-4 rounded-lg mb-4 transition-all duration-300 ${
        darkMode
          ? "bg-[#0D1117] border border-[#252525]"
          : "bg-white border border-[#E0E0E0] shadow-sm"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className={`text-lg font-bold ${
            darkMode ? "text-white" : "text-gray-800"
          }`}
        >
          Assessment Setup
        </h3>

        <div
          className={`text-xs px-2 py-1 rounded-full ${
            totalAssessments > 0
              ? darkMode
                ? "bg-green-900/30 text-green-300"
                : "bg-green-100 text-green-800"
              : darkMode
                ? "bg-yellow-900/30 text-yellow-300"
                : "bg-yellow-100 text-yellow-800"
          }`}
        >
          {totalAssessments} assessment{totalAssessments !== 1 ? "s" : ""}
        </div>
      </div>

      <p
        className={`text-xs mb-4 flex items-start ${
          darkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        <Info size={14} className="mr-1.5 mt-0.5 flex-shrink-0" />
        Configure the number of each assessment type for your marksheet
        template.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label
            className={`block text-sm mb-1 ${
              darkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Assignments
          </label>
          <input
            type="number"
            min="0"
            max="10"
            value={assessmentSetup.assignments}
            onChange={(e) => handleInputChange("assignments", e.target.value)}
            className={`w-full p-2 text-sm rounded border ${
              darkMode
                ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
            } ${validationErrors.assignments ? "border-red-500" : ""}`}
          />
          {validationErrors.assignments && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle size={10} className="mr-1" />{" "}
              {validationErrors.assignments}
            </p>
          )}
        </div>

        <div>
          <label
            className={`block text-sm mb-1 ${
              darkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Class Tests
          </label>
          <input
            type="number"
            min="0"
            max="5"
            value={assessmentSetup.classTests}
            onChange={(e) => handleInputChange("classTests", e.target.value)}
            className={`w-full p-2 text-sm rounded border ${
              darkMode
                ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
            } ${validationErrors.classTests ? "border-red-500" : ""}`}
          />
          {validationErrors.classTests && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle size={10} className="mr-1" />{" "}
              {validationErrors.classTests}
            </p>
          )}
        </div>

        <div>
          <label
            className={`block text-sm mb-1 ${
              darkMode ? "text-gray-300" : "text-gray-700"
            }`}
          >
            Projects
          </label>
          <input
            type="number"
            min="0"
            max="3"
            value={assessmentSetup.projects}
            onChange={(e) => handleInputChange("projects", e.target.value)}
            className={`w-full p-2 text-sm rounded border ${
              darkMode
                ? "bg-[#161B22] border-[#383838] text-white focus:ring-1 focus:ring-[#A41E22]"
                : "bg-white border-gray-300 focus:ring-1 focus:ring-[#A41E22]"
            } ${validationErrors.projects ? "border-red-500" : ""}`}
          />
          {validationErrors.projects && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <AlertCircle size={10} className="mr-1" />{" "}
              {validationErrors.projects}
            </p>
          )}
        </div>

        <div className="flex items-start pt-2">
          <input
            type="checkbox"
            id="includePRS"
            checked={assessmentSetup.includePRS}
            onChange={(e) =>
              setAssessmentSetup({
                ...assessmentSetup,
                includePRS: e.target.checked,
              })
            }
            className={`mr-2 h-4 w-4 mt-1 ${
              darkMode
                ? "accent-[#A41E22] bg-[#161B22] border-[#383838]"
                : "accent-[#A41E22]"
            }`}
          />
          <div>
            <label
              htmlFor="includePRS"
              className={`text-sm select-none ${darkMode ? "text-gray-300" : "text-gray-700"}`}
            >
              Include PRS
            </label>
            <p
              className={`text-xs mt-0.5 ${darkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Practical/Research/Seminar column
            </p>
          </div>
        </div>
      </div>

      {totalAssessments > 0 && (
        <div
          className={`p-3 rounded-lg mb-4 ${
            darkMode ? "bg-[#161B22]" : "bg-gray-50"
          }`}
        >
          <h4
            className={`text-sm font-medium mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}
          >
            Configuration Preview
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {assessmentSetup.assignments > 0 && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  darkMode
                    ? "bg-blue-900/30 text-blue-300"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {assessmentSetup.assignments} Assignment
                {assessmentSetup.assignments !== 1 ? "s" : ""}
              </span>
            )}
            {assessmentSetup.classTests > 0 && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  darkMode
                    ? "bg-purple-900/30 text-purple-300"
                    : "bg-purple-100 text-purple-800"
                }`}
              >
                {assessmentSetup.classTests} Test
                {assessmentSetup.classTests !== 1 ? "s" : ""}
              </span>
            )}
            {assessmentSetup.projects > 0 && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  darkMode
                    ? "bg-amber-900/30 text-amber-300"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {assessmentSetup.projects} Project
                {assessmentSetup.projects !== 1 ? "s" : ""}
              </span>
            )}
            {assessmentSetup.includePRS && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs ${
                  darkMode
                    ? "bg-green-900/30 text-green-300"
                    : "bg-green-100 text-green-800"
                }`}
              >
                PRS Included
              </span>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col-reverse md:flex-row justify-between items-center pt-3 gap-3">
        <div className="flex flex-col-reverse md:flex-row gap-2 w-full md:w-auto">
          <button
            className={`px-4 py-2 text-sm rounded font-medium flex items-center justify-center transition-colors ${
              totalAssessments === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : darkMode
                  ? "bg-[#252525] text-white hover:bg-[#383838]"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            onClick={onGenerateTemplate}
            disabled={totalAssessments === 0}
          >
            <Download size={16} className="mr-1.5" />
            Download Template
          </button>

          <button
            className={`px-4 py-2 text-sm rounded font-medium flex items-center justify-center transition-colors ${
              totalAssessments === 0
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : darkMode
                  ? "bg-[#A41E22] hover:bg-[#8A1A1E] text-white"
                  : "bg-[#A41E22] hover:bg-[#8A1A1E] text-white"
            }`}
            onClick={handleNext}
            disabled={totalAssessments === 0}
          >
            {totalAssessments > 0 ? (
              <>Next: Upload Marks</>
            ) : (
              <>
                <AlertCircle size={16} className="mr-1.5" />
                Configure Assessments First
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2AssessmentSetup;
